import { createSlice } from '@reduxjs/toolkit';

const STORAGE_KEY = 'healing.auth';

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persisted = loadFromStorage();

const initialState = persisted ?? {
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null,
  refreshTokenExpiresAt: null,
  user: null,
  loginAt: null, // epoch ms — set once on login, preserved across token refreshes
};

const persist = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokens(state, action) {
      const prevLoginAt = state.loginAt;
      Object.assign(state, action.payload);
      // Preserve loginAt across token refreshes; only stamp it on the very first login.
      state.loginAt = prevLoginAt || Date.now();
      persist(state);
    },
    setUser(state, action) {
      state.user = action.payload;
      persist(state);
    },
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.accessTokenExpiresAt = null;
      state.refreshTokenExpiresAt = null;
      state.user = null;
      state.loginAt = null;
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    },
  },
});

export const { setTokens, setUser, logout } = authSlice.actions;
export default authSlice.reducer;

export const selectIsAuthenticated = (s) => Boolean(s.auth?.accessToken);
export const selectCurrentUser = (s) => s.auth?.user ?? null;
export const selectRoles = (s) => s.auth?.user?.roles ?? [];
export const selectHasRole = (role) => (s) => (s.auth?.user?.roles ?? []).includes(role);
export const selectLoginAt = (s) => s.auth?.loginAt ?? null;
export const selectIsReceptionist = (s) => (s.auth?.user?.roles ?? []).includes('Receptionist');
